import { formatCurrencyShort, formatDate } from '../../lib/format.js';

/**
 * Rend 2 <td> distinctes côte à côte : Réel (gauche) | Prévi (droite).
 *
 * À utiliser à l'intérieur d'un <tr> — le composant retourne un Fragment.
 *
 * Props :
 *   forecastCents   number | null   — montant prévisionnel (centimes, signé)
 *   actualCents     number | null   — montant réel (centimes, signé)
 *   isFuture        boolean         — mois futur strict → réel = "—"
 *   flowType        'income' | 'expense' | 'neutral'  — détermine la couleur
 *   isClickable     boolean         — active cursor-pointer + hover
 *   onClickForecast () => void      — callback clic cellule Prévi
 *   onClickActual   () => void      — callback clic cellule Réel
 *   isOverrun       boolean         — liseré rouge sur la cellule Réel si vrai
 *   frozenAt        string | null   — date de capture du mois figé (ISO) → cadenas
 */
export default function CellPair({
  forecastCents,
  actualCents,
  isFuture = false,
  flowType = 'expense',
  isClickable = false,
  onClickForecast,
  onClickActual,
  isOverrun = false,
  frozenAt = null,
}) {
  const forecastColor = forecastColorClass(forecastCents, flowType);
  const actualColor   = actualColorClass(actualCents, flowType);

  const baseTd = 'min-w-[60px] px-1.5 py-1 text-[11px] tabular-nums text-right align-middle';

  // Cellule Réel (gauche) — couleur pleine, plus marqué visuellement
  const actualTd = [
    baseTd,
    actualColor,
    'font-medium',
    'border-r border-slate-100',
    isOverrun ? 'border-l-2 border-l-red-400 bg-red-50/40' : '',
    isFuture ? 'bg-slate-50/30' : '',
    isClickable ? 'cursor-pointer hover:bg-slate-100/70' : '',
  ].filter(Boolean).join(' ');

  // Cellule Prévi (droite) — italique léger, couleur atténuée, fond légèrement teinté futur
  const forecastTd = [
    baseTd,
    'italic',
    forecastColor,
    'border-r border-slate-100 last:border-r-0',
    isFuture ? 'bg-slate-50/40' : '',
    isClickable ? 'cursor-pointer hover:bg-slate-100/70' : '',
  ].filter(Boolean).join(' ');

  const forecastDisplay =
    forecastCents != null ? formatCurrencyShort(forecastCents) : '—';

  // Le title du <td> l'emporte au survol sur celui d'un enfant SVG : la date de
  // gel est donc portée par la cellule elle-même, et répétée sur un <span>
  // (lisible au survol de l'icône et par les lecteurs d'écran).
  const frozenLabel   = frozenAt ? `Figé le ${formatDate(frozenAt.slice(0, 10))}` : null;
  const forecastTitle = frozenLabel ? `Prévisionnel · ${frozenLabel}` : 'Prévisionnel';

  const actualDisplay = isFuture
    ? <span className="text-slate-300">—</span>
    : (actualCents != null ? formatCurrencyShort(actualCents) : '—');

  return (
    <>
      <td
        className={actualTd}
        onClick={isClickable && onClickActual ? onClickActual : undefined}
        title={isOverrun ? 'Dépassement de budget' : 'Réel'}
      >
        {actualDisplay}
      </td>
      <td
        className={forecastTd}
        onClick={isClickable && onClickForecast ? onClickForecast : undefined}
        title={forecastTitle}
      >
        {forecastDisplay}
      </td>
    </>
  );
}

// ── Helpers couleur ────────────────────────────────────────────────────────────

/**
 * Couleur atténuée pour la colonne Prévi.
 * income positif → émeraude atténué ; expense négatif → rouge atténué ; neutre → slate.
 */
export function forecastColorClass(cents, flowType) {
  if (cents == null) return 'text-slate-400';
  if (flowType === 'income') {
    return cents >= 0 ? 'text-emerald-600/70' : 'text-red-500/70';
  }
  if (flowType === 'expense') {
    return cents <= 0 ? 'text-red-600/70' : 'text-emerald-600/70';
  }
  // neutral
  return cents >= 0 ? 'text-slate-500' : 'text-slate-500';
}

/**
 * Couleur pleine pour la colonne Réel.
 * income positif → émeraude ; expense négatif → rouge ; neutre → slate.
 */
export function actualColorClass(cents, flowType) {
  if (cents == null) return 'text-slate-500';
  if (flowType === 'income') {
    return cents >= 0 ? 'text-emerald-700' : 'text-red-700';
  }
  if (flowType === 'expense') {
    return cents <= 0 ? 'text-red-700' : 'text-emerald-700';
  }
  // neutral
  return cents >= 0 ? 'text-slate-700' : 'text-slate-700';
}
